import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { fmtIstDate } from '../utils/dateUtils';
import { inr, qty, SummaryCard, Pager } from './brandstoreAdminShared';

// Read-only view of every company's stock ledger (purchase entries, sales, inventory).
// Entries are created by each company's own store admin in brandstore-frontend — this tab
// never writes. Only companies onboarded with "Enable Inventory Module" = Yes appear.

const PAGE_SIZE = 20;
const VIEWS = [
  { key: 'purchases', label: '🧾 Purchase Entries' },
  { key: 'sales',     label: '🛒 Sales' },
  { key: 'inventory', label: '📦 Inventory' },
];

const fmtDate = (d) => fmtIstDate(d, { day: '2-digit', month: 'short', year: 'numeric' });
/** "Brown / M" — or the variant label, or "Standard" for a product with no variants. */
const variantText = (item) => [item.color, item.size].filter(Boolean).join(' / ') || item.label || (item.variantId ? 'Variant' : 'Standard');

const input = { padding: '8px 10px', border: '1.5px solid #E5E7EB', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none', color: '#111827', background: '#fff' };
const th = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.4px', borderBottom: '1.5px solid #E5E7EB', whiteSpace: 'nowrap' };
const thNum = { ...th, textAlign: 'right' };
const td = { padding: '10px 12px', fontSize: 13, color: '#111827', borderBottom: '1px solid #F3F4F6', verticalAlign: 'top' };
const tdNum = { ...td, textAlign: 'right', whiteSpace: 'nowrap' };

/** "Invoice" cell: opens the PDF the company's admin uploaded for this entry (view-only here —
 *  uploading happens in brandstore-frontend). entryPath is e.g. `/admin/brandstore/stock/purchases/12`.
 *  The link is fetched on click because it expires within minutes; the tab is opened first so the
 *  browser doesn't treat it as an unrequested popup. */
function InvoiceLink({ entry, entryPath }) {
    if (!entry.invoiceFileName) return <span style={{ color: '#9CA3AF' }}>—</span>;
    const view = async () => {
        const win = window.open('', '_blank');
        try {
            const r = await api.get(`${entryPath}/invoice`);
            if (win) win.location.href = r.data.url;
            else window.location.href = r.data.url;
        } catch (err) {
            win?.close();
            window.alert(err.response?.data?.error || 'Could not open the invoice.');
        }
    };
    return (
        <button onClick={view} title={entry.invoiceFileName}
                style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#F29213' }}>
            View invoice
        </button>
    );
}

/** In Stock cell: the live count coloured by level, or "Not tracked" when there's no count. */
const filterLabel = { fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 };

/** A filter pick list you can type into: shows every option, narrows as you type, and a
 *  leading "All" entry clears it. Used for Inventory's Colour and Size filters. */
function FilterSearchSelect({ label, options, value, onChange, placeholder = 'All', width = 150 }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const q = query.trim().toLowerCase();
  const shown = q ? options.filter(o => o.toLowerCase().includes(q)) : options;
  const choose = (v) => { onChange(v); setQuery(''); setOpen(false); };
  return (
    <div style={{ position: 'relative', width }}>
      <div style={filterLabel}>{label}</div>
      <input
        style={{ ...input, width: '100%', boxSizing: 'border-box' }}
        value={open ? query : value}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={e => {
          if (e.key === 'Enter') { e.preventDefault(); if (shown[0]) choose(shown[0]); }
          if (e.key === 'Escape') { setOpen(false); setQuery(''); }
        }}
      />
      {open && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: 2, maxHeight: 240, overflowY: 'auto', background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 7, boxShadow: '0 6px 20px rgba(0,0,0,0.12)' }}>
          <div onMouseDown={e => { e.preventDefault(); choose(''); }}
            style={{ padding: '7px 12px', cursor: 'pointer', fontSize: 13, color: '#6B7280', borderBottom: '1px solid #F3F4F6' }}>All</div>
          {shown.length === 0 ? (
            <div style={{ padding: '7px 12px', fontSize: 12, color: '#9CA3AF' }}>No match</div>
          ) : shown.map(o => (
            <div key={o} onMouseDown={e => { e.preventDefault(); choose(o); }}
              style={{ padding: '7px 12px', cursor: 'pointer', fontSize: 13, background: o === value ? '#FFF7ED' : '#fff', fontWeight: o === value ? 600 : 400 }}>
              {o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A min–max pair of number boxes under one label. */
function PriceRange({ label, min, max, onMin, onMax }) {
  const box = { ...input, width: 84, boxSizing: 'border-box' };
  return (
    <div>
      <div style={filterLabel}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <input type="number" min="0" style={box} placeholder="Min" value={min} onChange={e => onMin(e.target.value)} />
        <span style={{ color: '#9CA3AF' }}>–</span>
        <input type="number" min="0" style={box} placeholder="Max" value={max} onChange={e => onMax(e.target.value)} />
      </div>
    </div>
  );
}

function StockCell({ value }) {
  if (value == null) {
    return <td style={{ ...tdNum, fontSize: 12, color: '#9CA3AF' }} title="No Qty set for this SKU in the product form">Not tracked</td>;
  }
  return <td style={{ ...tdNum, fontWeight: 700, color: value < 0 ? '#B91C1C' : value === 0 ? '#9CA3AF' : '#15803D' }}>{qty(value)}</td>;
}


/** Read-only list of hand-entered sale entries (stock a company sold outside its storefront),
 *  shown above the storefront orders in the Sales view. Entries are created by each company's
 *  store admin in brandstore-frontend; this only lists them. Filters come from the parent. */
function SaleEntriesReadOnly({ tenantId, from, to, search, showCompany, tenantName }) {
  const [page, setPage]         = useState(0);
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { setPage(0); }, [tenantId, from, to, search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    const params = { page, size: PAGE_SIZE, search };
    if (tenantId) params.tenantId = tenantId;
    if (from) params.from = from;
    if (to) params.to = to;
    api.get('/admin/brandstore/stock/sale-entries', { params })
      .then(r => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(err => { if (!cancelled) { setError(err.response?.data?.error || 'Failed to load sale entries.'); setData(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [tenantId, from, to, search, page]);

  const rows = data?.content || [];
  const totals = data?.totals || {};
  const cols = showCompany ? 10 : 9;

  return (
    <div style={{ marginBottom: 28 }}>
      <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800, color: '#111827' }}>Sale entries</h3>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <SummaryCard label="Entries" value={qty(totals.entries)} />
        <SummaryCard label="Taxable value" value={inr(totals.taxableAmount)} />
        <SummaryCard label="GST" value={inr(totals.gstAmount)} />
        <SummaryCard label="Total value" value={inr(totals.totalAmount)} />
      </div>
      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Loading…</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#B91C1C' }}>{error}</div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>No sale entries match these filters.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead><tr>
                <th style={th}></th>
                {showCompany && <th style={th}>Company</th>}
                <th style={th}>Invoice No.</th>
                <th style={th}>Customer</th>
                <th style={th}>Sale Date</th>
                <th style={thNum}>Qty</th>
                <th style={thNum}>Taxable</th>
                <th style={thNum}>GST</th>
                <th style={thNum}>Total</th>
                <th style={th}>Invoice</th>
              </tr></thead>
              <tbody>
                {rows.map(e => (
                  <React.Fragment key={e.id}>
                    <tr onClick={() => setExpanded(expanded === e.id ? null : e.id)} style={{ cursor: 'pointer' }}>
                      <td style={{ ...td, color: '#9CA3AF', width: 20 }}>{expanded === e.id ? '▾' : '▸'}</td>
                      {showCompany && <td style={td}>{tenantName(e.tenantId)}</td>}
                      <td style={{ ...td, fontWeight: 600 }}>{e.invoiceNumber}</td>
                      <td style={td}>{e.customerName}</td>
                      <td style={td}>{fmtDate(e.saleDate)}</td>
                      <td style={tdNum}>{qty(e.totalQuantity)}</td>
                      <td style={tdNum}>{inr(e.taxableAmount)}</td>
                      <td style={tdNum}>{inr(e.gstAmount)}</td>
                      <td style={{ ...tdNum, fontWeight: 700 }}>{inr(e.totalAmount)}</td>
                      <td style={td} onClick={ev => ev.stopPropagation()}>
                        <InvoiceLink entry={e} entryPath={`/admin/brandstore/stock/sale-entries/${e.id}`} />
                      </td>
                    </tr>
                    {expanded === e.id && (
                      <tr><td colSpan={cols} style={{ ...td, background: '#F9FAFB', padding: '8px 16px 14px 40px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr>
                            <th style={th}>SKU</th>
                            <th style={th}>Product</th>
                            <th style={th}>Colour / Size</th>
                            <th style={thNum}>Qty</th>
                            <th style={thNum}>Sale Price</th>
                            <th style={thNum}>GST %</th>
                            <th style={thNum}>Taxable</th>
                            <th style={thNum}>GST</th>
                            <th style={thNum}>Total</th>
                          </tr></thead>
                          <tbody>
                            {e.lines.map((l, i) => (
                              <tr key={i}>
                                <td style={{ ...td, fontSize: 12, color: '#6B7280' }}>{l.sku || '—'}</td>
                                <td style={td}>{l.productName}</td>
                                <td style={td}>{variantText(l)}</td>
                                <td style={tdNum}>{qty(l.quantity)}</td>
                                <td style={tdNum}>{inr(l.unitPrice)}</td>
                                <td style={tdNum}>{Number(l.gstPercent)}%</td>
                                <td style={tdNum}>{inr(l.taxableAmount)}</td>
                                <td style={tdNum}>{inr(l.gstAmount)}</td>
                                <td style={{ ...tdNum, fontWeight: 600 }}>{inr(l.totalAmount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {e.notes && <p style={{ fontSize: 12, color: '#6B7280', margin: '10px 0 0' }}>Notes: {e.notes}</p>}
                        <p style={{ fontSize: 11, color: '#9CA3AF', margin: '8px 0 0' }}>
                          Entered by {e.createdBy || '—'}{e.updatedBy ? ` · last edited by ${e.updatedBy}` : ''}
                        </p>
                      </td></tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && (
          <Pager page={data?.page || 0} totalPages={data?.totalPages || 0} totalElements={data?.totalElements || 0} onPage={setPage} noun="entries" />
        )}
      </div>
    </div>
  );
}

export default function BrandstoreStockTab({ tenants }) {
  const enabledTenants = tenants.filter(t => t.showStockPurchase);
  const [view, setView]           = useState('purchases');
  const [tenantId, setTenantId]   = useState('');
  const [from, setFrom]           = useState('');
  const [to, setTo]               = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(0);
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [expanded, setExpanded]   = useState(null);
  // Inventory-only filters. Prices are typed, so they're debounced like the search box.
  const [color, setColor]         = useState('');
  const [size, setSize]           = useState('');
  const [priceInput, setPriceInput] = useState({ minBuy: '', maxBuy: '', minSell: '', maxSell: '' });
  const [prices, setPrices]       = useState(priceInput);
  const [facets, setFacets]       = useState({ colors: [], sizes: [] });

  // Debounce the search box so every keystroke doesn't hit the server.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    const id = setTimeout(() => setPrices(priceInput), 350);
    return () => clearTimeout(id);
  }, [priceInput]);
  const setPriceField = (k, v) => setPriceInput(p => ({ ...p, [k]: v }));

  useEffect(() => { setPage(0); }, [view, tenantId, from, to, search, color, size, prices]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    const params = { page, size: PAGE_SIZE, search };
    if (tenantId) params.tenantId = tenantId;
    // Inventory has no date filter: Purchased/Sold are all-time and In Stock is the live count.
    if (view !== 'inventory') {
      if (from) params.from = from;
      if (to) params.to = to;
    }
    // Inventory is listed one row per company+product (its SKUs nested under "skus"), paged by product.
    if (view === 'inventory') {
      params.group = 'product';
      if (color) params.color = color;
      if (size) params.variantSize = size; // "size" is the page size
      if (prices.minBuy !== '') params.minPurchasePrice = prices.minBuy;
      if (prices.maxBuy !== '') params.maxPurchasePrice = prices.maxBuy;
      if (prices.minSell !== '') params.minSalePrice = prices.minSell;
      if (prices.maxSell !== '') params.maxSalePrice = prices.maxSell;
    }
    api.get(`/admin/brandstore/stock/${view}`, { params })
      .then(r => {
        if (cancelled) return;
        setData({ ...r.data, view });
        if (r.data.facets) setFacets(r.data.facets); // kept across loads so the pick lists don't flicker
        setLoading(false);
      })
      .catch(err => { if (!cancelled) { setError(err.response?.data?.error || 'Failed to load.'); setData(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [view, tenantId, from, to, search, page, color, size, prices]);

  const tenantName = (id) => tenants.find(t => t.id === id)?.displayName || `#${id}`;
  const showCompany = !tenantId;
  // Switching views renders once before the new fetch starts, with the previous view's rows
  // still in `data` — e.g. purchase entries (no `status`) fed to the Sales table, which crashed
  // the whole tab. Only use data that was actually loaded for the view being shown.
  const current = data && data.view === view ? data : null;
  const rows = current?.content || [];
  const totals = current?.totals || {};

  const viewBtn = (v) => (
    <button key={v.key} onClick={() => setView(v.key)} style={{
      padding: '7px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
      background: view === v.key ? '#111827' : '#fff',
      color: view === v.key ? '#fff' : '#4B5563',
      boxShadow: view === v.key ? 'none' : '0 1px 4px rgba(0,0,0,0.07)',
    }}>{v.label}</button>
  );

  const clearFilters = () => {
    setTenantId(''); setFrom(''); setTo(''); setSearchInput('');
    setColor(''); setSize(''); setPriceInput({ minBuy: '', maxBuy: '', minSell: '', maxSell: '' });
  };
  const inventoryFiltered = view === 'inventory' && (color || size || Object.values(priceInput).some(v => v !== ''));
  const hasFilters = tenantId || (view !== 'inventory' && (from || to)) || searchInput || inventoryFiltered;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{VIEWS.map(viewBtn)}</div>
        <span style={{ fontSize: 12, color: '#9CA3AF' }}>Read-only — entries are managed by each company's store admin</span>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16, background: '#fff', padding: 14, borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Company</div>
          <select style={{ ...input, minWidth: 180 }} value={tenantId} onChange={e => setTenantId(e.target.value)}>
            <option value="">All companies ({enabledTenants.length})</option>
            {enabledTenants.map(t => <option key={t.id} value={t.id}>{t.displayName}</option>)}
          </select>
        </div>
        {view !== 'inventory' && (
          <>
            <div>
              <div style={filterLabel}>From</div>
              <input type="date" style={input} value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} />
            </div>
            <div>
              <div style={filterLabel}>To</div>
              <input type="date" style={input} value={to} min={from || undefined} onChange={e => setTo(e.target.value)} />
            </div>
          </>
        )}
        {view === 'inventory' && (
          <>
            <FilterSearchSelect label="Colour" options={facets.colors} value={color} onChange={setColor} />
            <FilterSearchSelect label="Size" options={facets.sizes} value={size} onChange={setSize} />
            <PriceRange label="Purchase price (₹)" min={priceInput.minBuy} max={priceInput.maxBuy}
              onMin={v => setPriceField('minBuy', v)} onMax={v => setPriceField('maxBuy', v)} />
            <PriceRange label="Sale price (₹)" min={priceInput.minSell} max={priceInput.maxSell}
              onMin={v => setPriceField('minSell', v)} onMax={v => setPriceField('maxSell', v)} />
          </>
        )}
        <div style={{ flex: '1 1 220px' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Search</div>
          <input
            style={{ ...input, width: '100%', boxSizing: 'border-box' }}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder={view === 'purchases' ? 'Invoice no., vendor, product, SKU…' : view === 'sales' ? 'Invoice no., customer, product, SKU, employee…' : 'Product, SKU, colour, size…'}
          />
        </div>
        {hasFilters && (
          <button onClick={clearFilters} style={{ ...input, cursor: 'pointer', fontWeight: 600, color: '#6B7280' }}>Clear</button>
        )}
      </div>

      {enabledTenants.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#9CA3AF', background: '#fff', borderRadius: 10 }}>
          No company has "Enable Inventory Module" turned on yet. Enable it from the company's settings in the Companies tab.
        </div>
      ) : (
        <>
          {/* Sales view: hand-entered sale entries first, storefront orders below. */}
          {view === 'sales' && (
            <>
              <SaleEntriesReadOnly tenantId={tenantId} from={from} to={to} search={search}
                showCompany={showCompany} tenantName={tenantName} />
              <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800, color: '#111827' }}>Store orders</h3>
            </>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            {view === 'purchases' && <>
              <SummaryCard label="Entries" value={qty(totals.entries)} />
              <SummaryCard label="Taxable value" value={inr(totals.taxableAmount)} />
              <SummaryCard label="GST" value={inr(totals.gstAmount)} />
              <SummaryCard label="Total value" value={inr(totals.totalAmount)} />
            </>}
            {view === 'sales' && <>
              <SummaryCard label="Line items" value={qty(totals.lineItems)} />
              <SummaryCard label="Units sold" value={qty(totals.units)} />
              <SummaryCard label="Sale value" value={inr(totals.valueInr)} />
            </>}
            {view === 'inventory' && <>
              <SummaryCard label="Products" value={qty(totals.products)} />
              <SummaryCard label="SKUs" value={qty(totals.items)} />
              <SummaryCard label="Units purchased" value={qty(totals.purchasedQty)} />
              <SummaryCard label="Units sold" value={qty(totals.soldQty)} />
              <SummaryCard label="In stock now" value={qty(totals.inStock)} tone={totals.inStock < 0 ? '#B91C1C' : undefined} />
              <SummaryCard label="Stock value" value={inr(totals.stockValue)} />
            </>}
          </div>

          <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            {loading || (!current && !error) ? (
              <div style={{ textAlign: 'center', padding: 50, color: '#9CA3AF' }}>Loading…</div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: 50, color: '#B91C1C' }}>{error}</div>
            ) : rows.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 50, color: '#9CA3AF' }}>Nothing matches these filters.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                {view === 'purchases' && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                    <thead><tr>
                      <th style={th}></th>
                      {showCompany && <th style={th}>Company</th>}
                      <th style={th}>Invoice No.</th>
                      <th style={th}>Vendor</th>
                      <th style={th}>Punched Date</th>
                      <th style={thNum}>Qty</th>
                      <th style={thNum}>Taxable</th>
                      <th style={thNum}>GST</th>
                      <th style={thNum}>Total</th>
                      <th style={th}>Entered By</th>
                      <th style={th}>Invoice</th>
                    </tr></thead>
                    <tbody>
                      {rows.map(e => (
                        <React.Fragment key={e.id}>
                          <tr onClick={() => setExpanded(expanded === e.id ? null : e.id)} style={{ cursor: 'pointer' }}>
                            <td style={{ ...td, color: '#9CA3AF', width: 20 }}>{expanded === e.id ? '▾' : '▸'}</td>
                            {showCompany && <td style={td}>{tenantName(e.tenantId)}</td>}
                            <td style={{ ...td, fontWeight: 600 }}>{e.invoiceNumber}</td>
                            <td style={td}>{e.vendorName}</td>
                            <td style={td}>{fmtDate(e.punchedDate)}</td>
                            <td style={tdNum}>{qty(e.totalQuantity)}</td>
                            <td style={tdNum}>{inr(e.taxableAmount)}</td>
                            <td style={tdNum}>{inr(e.gstAmount)}</td>
                            <td style={{ ...tdNum, fontWeight: 700 }}>{inr(e.totalAmount)}</td>
                            <td style={{ ...td, fontSize: 12, color: '#6B7280' }}>{e.createdBy || '—'}</td>
                            <td style={td} onClick={ev => ev.stopPropagation()}>
                              <InvoiceLink entry={e} entryPath={`/admin/brandstore/stock/purchases/${e.id}`} />
                            </td>
                          </tr>
                          {expanded === e.id && (
                            <tr><td colSpan={showCompany ? 11 : 10} style={{ ...td, background: '#F9FAFB', padding: '8px 16px 14px 40px' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead><tr>
                                  <th style={th}>SKU</th>
                                  <th style={th}>Product</th>
                                  <th style={th}>Colour / Size</th>
                                  <th style={thNum}>Qty</th>
                                  <th style={thNum}>Unit Price</th>
                                  <th style={thNum}>GST %</th>
                                  <th style={thNum}>Taxable</th>
                                  <th style={thNum}>GST</th>
                                  <th style={thNum}>Total</th>
                                </tr></thead>
                                <tbody>
                                  {e.lines.map((l, i) => (
                                    <tr key={i}>
                                      <td style={{ ...td, fontSize: 12, color: '#6B7280' }}>{l.sku || '—'}</td>
                                      <td style={td}>{l.productName}</td>
                                      <td style={td}>{variantText(l)}</td>
                                      <td style={tdNum}>{qty(l.quantity)}</td>
                                      <td style={tdNum}>{inr(l.unitPrice)}</td>
                                      <td style={tdNum}>{Number(l.gstPercent)}%</td>
                                      <td style={tdNum}>{inr(l.taxableAmount)}</td>
                                      <td style={tdNum}>{inr(l.gstAmount)}</td>
                                      <td style={{ ...tdNum, fontWeight: 600 }}>{inr(l.totalAmount)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              {e.notes && <p style={{ fontSize: 12, color: '#6B7280', margin: '10px 0 0' }}>Notes: {e.notes}</p>}
                            </td></tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                )}

                {view === 'sales' && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                    <thead><tr>
                      <th style={th}>Date</th>
                      {showCompany && <th style={th}>Company</th>}
                      <th style={th}>Order #</th>
                      <th style={th}>SKU</th>
                      <th style={th}>Product</th>
                      <th style={thNum}>Qty</th>
                      <th style={th}>Employee</th>
                      <th style={th}>Campaign</th>
                      <th style={thNum}>Value</th>
                      <th style={th}>Status</th>
                    </tr></thead>
                    <tbody>
                      {rows.map(s => (
                        <tr key={s.id}>
                          <td style={td}>{fmtDate(s.createdAt)}</td>
                          {showCompany && <td style={td}>{tenantName(s.tenantId)}</td>}
                          <td style={td}>#{s.id}</td>
                          <td style={{ ...td, fontSize: 12, color: '#6B7280' }}>{s.sku || '—'}</td>
                          <td style={td}>
                            {s.productName}
                            {(s.selectedSize || s.selectedColor) && <span style={{ color: '#9CA3AF', fontSize: 12 }}> ({[s.selectedSize, s.selectedColor].filter(Boolean).join('/')})</span>}
                          </td>
                          <td style={tdNum}>{qty(s.quantity)}</td>
                          <td style={{ ...td, fontSize: 12 }}>{s.employeeEmail}</td>
                          <td style={td}>{s.campaignName || '—'}</td>
                          <td style={tdNum}>{inr(s.valueInr)}</td>
                          <td style={{ ...td, fontSize: 11, fontWeight: 700, color: '#6B7280' }}>{(s.status || '').replace(/_/g, ' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {view === 'inventory' && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                    <thead><tr>
                      <th style={th}></th>
                      {showCompany && <th style={th}>Company</th>}
                      <th style={th}>Product</th>
                      <th style={th}>Variants</th>
                      <th style={thNum}>Purchase Price</th>
                      <th style={thNum}>GST %</th>
                      <th style={thNum}>Sale Price</th>
                      <th style={thNum}>Total Purchased</th>
                      <th style={thNum}>Total Sold</th>
                      <th style={thNum}>Available Stock</th>
                      <th style={thNum}>Total Stock Value</th>
                    </tr></thead>
                    <tbody>
                      {rows.map(p => {
                        const rowKey = `inv:${p.tenantId}:${p.productId}`;
                        const open = expanded === rowKey;
                        const shown = p.variants.slice(0, 4);
                        return (
                          <React.Fragment key={rowKey}>
                            <tr onClick={() => setExpanded(open ? null : rowKey)} style={{ cursor: 'pointer' }}>
                              <td style={{ ...td, color: '#9CA3AF', width: 20 }}>{open ? '▾' : '▸'}</td>
                              {showCompany && <td style={td}>{tenantName(p.tenantId)}</td>}
                              <td style={{ ...td, fontWeight: 600 }}>{p.productName}</td>
                              <td style={{ ...td, fontSize: 12, color: '#4B5563' }}>
                                {p.variants.length === 0 ? '—' : shown.join(', ')}
                                {p.variants.length > shown.length && <span style={{ color: '#9CA3AF' }}> +{p.variants.length - shown.length} more</span>}
                              </td>
                              <td style={tdNum}>{p.purchasePrice != null ? inr(p.purchasePrice) : '—'}</td>
                              <td style={tdNum}>{p.gstPercent != null ? `${Number(p.gstPercent)}%` : '—'}</td>
                              <td style={tdNum}>{p.salePrice != null ? inr(p.salePrice) : '—'}</td>
                              <td style={tdNum}>{qty(p.purchasedQty)}</td>
                              <td style={tdNum}>{qty(p.soldQty)}</td>
                              <StockCell value={p.inStock} />
                              <td style={{ ...tdNum, fontWeight: 700 }}>{inr(p.stockValue)}</td>
                            </tr>
                            {open && (
                              <tr><td colSpan={showCompany ? 11 : 10} style={{ ...td, background: '#F9FAFB', padding: '8px 16px 14px 40px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                  <thead><tr>
                                    <th style={th}>SKU</th>
                                    <th style={th}>Product</th>
                                    <th style={th}>Size / Colour</th>
                                    <th style={thNum}>Purchased</th>
                                    <th style={thNum}>Sold</th>
                                    <th style={thNum}>Stock</th>
                                    <th style={thNum}>Stock Value</th>
                                  </tr></thead>
                                  <tbody>
                                    {p.skus.map(r => (
                                      <tr key={`${r.productId}:${r.variantId ?? ''}:${r.sku ?? ''}`}>
                                        <td style={{ ...td, fontSize: 12, color: '#6B7280' }}>{r.sku || '—'}</td>
                                        <td style={td}>{r.productName}</td>
                                        <td style={td}>{variantText(r)}</td>
                                        <td style={tdNum}>{qty(r.purchasedQty)}</td>
                                        <td style={tdNum}>{qty(r.soldQty)}</td>
                                        <StockCell value={r.inStock} />
                                        <td style={tdNum}>{inr(r.stockValue)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td></tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {!loading && !error && (
              <Pager page={current?.page || 0} totalPages={current?.totalPages || 0} totalElements={current?.totalElements || 0} onPage={setPage}
                noun={view === 'purchases' ? 'entries' : view === 'sales' ? 'line items' : 'products'} />
            )}
          </div>
          {view === 'inventory' && (
            <p style={{ fontSize: 11, color: '#9CA3AF', margin: '8px 2px 0' }}>
              One row per product — click it to see each SKU. Lists items in active campaigns only — past orders and purchases of ended-campaign or deleted products stay in the Sales and Purchase Entries tabs. In Stock is the live count on the product right now: purchase entries add to it, orders take from it, cancellations and returns put units back. "Not tracked" means the SKU has no Qty set in the product form yet — fill in a Qty for every size/colour of that product to start tracking. Stock Value = In Stock × the product's purchase price (the Unit Price last entered) plus the product's GST %. Purchased and Sold are all-time totals (cancelled, returned and unpaid orders excluded). Purchase and Sale Price are the product's prices excl. GST; Stock Value = In Stock × Purchase Price × (1 + GST %).
            </p>
          )}
        </>
      )}
    </div>
  );
}
